/* @jsxImportSource @emotion/react */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'

import { AgentRow } from '../../../../components/AgentRow'
import { useIsMobile } from '../../../../utils/responsive'
import { blockDurationMs, groupBlocks } from './helpers'
import type { Block, StoredBlock } from './types'

// On mobile the composer height is fixed (no spring resize as scenarios
// cycle). Tall enough to fit the biggest single block; smaller items just
// leave empty space above (justify-end pins the visible card to the bottom).
const MOBILE_COL_HEIGHT = 200

export type DemoAgentHandle = {
  pushBlock: (block: Block) => Promise<void>
  show: () => void
  hide: () => void
  reset: () => void
}

// ─── Tuning ─────────────────────────────────────────────────────────────

const EASE = [0.4, 0, 0.2, 1] as const
const HEIGHT_SPRING = {
  type: 'spring',
  stiffness: 260,
  damping: 34,
  mass: 0.9,
} as const

const agentBlockCss = css`
  border: 1.5px solid var(--saturn-orange);

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    z-index: -1;
    border-radius: inherit;
    background: rgba(255, 130, 77, 0.3);
    filter: blur(12px);
  }
`

const renderBlock = (b: StoredBlock) =>
  b.type === 'text' ? (
    <AgentRow
      icon={b.icon}
      title={b.text}
      pulse={b.animation === 'tool'}
      stream={b.animation === 'stream'}
      durationMs={b.duration}
    >
      {b.children}
    </AgentRow>
  ) : b.title ? (
    <AgentRow
      icon={b.icon}
      title={b.title}
      pulse={b.animation === 'tool'}
      stream={b.animation === 'stream'}
      durationMs={b.duration}
    >
      {b.children}
      {b.node}
    </AgentRow>
  ) : (
    b.node
  )

export const DemoAgent = forwardRef<DemoAgentHandle>((_props, ref) => {
  const [blocks, setBlocks] = useState<StoredBlock[]>([])
  const [visible, setVisible] = useState(false)
  const [colHeight, setColHeight] = useState(0)
  const idRef = useRef(0)
  const colRef = useRef<HTMLDivElement>(null)
  const timersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set())
  const isMobile = useIsMobile()

  const items = useMemo(() => groupBlocks(blocks), [blocks])
  const visibleItems = useMemo(() => items.slice(-1), [items])

  useLayoutEffect(() => {
    const col = colRef.current
    setColHeight(col && blocks.length > 0 ? col.offsetHeight : 0)
  }, [blocks])

  useEffect(() => {
    const col = colRef.current
    if (!col) return
    const ro = new ResizeObserver(() => setColHeight(col.offsetHeight))
    ro.observe(col)
    return () => ro.disconnect()
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      pushBlock: (block) =>
        new Promise<void>((resolve) => {
          setBlocks((prev) => [
            ...prev,
            { ...block, id: `block-${idRef.current++}` },
          ])
          const timer = setTimeout(() => {
            timersRef.current.delete(timer)
            resolve()
          }, blockDurationMs(block))
          timersRef.current.add(timer)
        }),
      show: () => setVisible(true),
      hide: () => setVisible(false),
      reset: () => {
        timersRef.current.forEach(clearTimeout)
        timersRef.current.clear()
        setBlocks([])
      },
    }),
    []
  )

  const hasBlocks = blocks.length > 0

  return (
    <motion.div
      className="position-relative width-full"
      style={{ pointerEvents: 'none' }}
      initial={false}
      animate={{
        opacity: visible ? 1 : 0,
        y: visible ? 0 : 8,
        scale: visible ? 1 : 0.98,
      }}
      transition={{ duration: 0.5, ease: EASE }}
    >
      <motion.div
        initial={false}
        animate={{ opacity: hasBlocks ? 1 : 0, y: hasBlocks ? 0 : 8 }}
        transition={{ duration: 0.3, ease: EASE }}
      >
        <motion.div
          className="flexbox flexbox-column flexbox-justify-end"
          animate={{ height: isMobile ? MOBILE_COL_HEIGHT : colHeight }}
          // Mobile uses a fixed height (no animation) so layout doesn't
          // shift as scenarios cycle; desktop keeps the springy resize.
          transition={isMobile ? { duration: 0 } : HEIGHT_SPRING}
        >
          <div ref={colRef}>
            <AnimatePresence initial={false}>
              {visibleItems.map((item) => (
                <motion.div
                  key={item.kind === 'group' ? item.groupId : item.block.id}
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16, scale: 0.98 }}
                  transition={{ duration: 0.32, ease: EASE }}
                  style={{ width: '100%' }}
                >
                  <div
                    css={agentBlockCss}
                    className={classNames(
                      'flexbox flexbox-column flexbox-gap-20 margin-top-12 flexbox-no-shrink position-relative',
                      'background-shade-100 radius-semilarger',
                      'padding-horizontal-16 padding-vertical-12'
                    )}
                    style={{
                      fontSize: 15,
                      lineHeight: '1.5em',
                    }}
                  >
                    {item.kind === 'group' ? (
                      <div className="flexbox flexbox-column flexbox-gap-12">
                        <AnimatePresence initial={false}>
                          {item.blocks.map((b) => (
                            <motion.div
                              key={b.id}
                              layout
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.28, ease: EASE }}
                            >
                              {renderBlock(b)}
                            </motion.div>
                          ))}
                        </AnimatePresence>
                      </div>
                    ) : (
                      renderBlock(item.block)
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  )
})

DemoAgent.displayName = 'DemoAgent'
